import { useCallback, useEffect, useRef, useState } from "react";
import useWebsocket, { ReadyState } from "react-use-websocket";

export { ReadyState };

/**
 * Platform-agnostic core for an authenticated, route-keyed WebSocket
 * subscription, shared by the web dashboard and the React Native app. Each
 * platform wraps this and injects its own `websocketUrl`, `token`, and `userId`
 * (and, on web, borrower-session overrides + a visibility re-register).
 *
 * Message delivery is callback-first: incoming messages are routed straight to
 * the caller's `onMessage` from inside `react-use-websocket`'s own callback
 * (synchronous, in-order, latest-ref so no rebinds), so back-to-back messages
 * are never dropped (`lastJsonMessage` only holds the most recent).
 * `lastMessage` is still exposed for older call sites. The returned `register`
 * lets a wrapper re-subscribe on demand (e.g. web tab-visibility regain).
 *
 * A `routeKey` of `"__open__"` (or undefined) just opens the socket without
 * registering or routing.
 */
export function useAuthenticatedWebsocketCore<T = any>({
  websocketUrl,
  token,
  userId,
  routeKey,
  enabled = true,
  sessionId,
  onMessage,
  onOpen,
}: {
  websocketUrl: string;
  token?: string | null;
  userId?: string | null;
  routeKey?: string;
  enabled?: boolean;
  sessionId?: string | null;
  onMessage?: (msg: T) => void;
  onOpen?: () => void;
}): { lastMessage: T | null; readyState: ReadyState; register: () => void } {
  const didUnmount = useRef(false);

  // Latest-refs so callers don't need useCallback and we don't rebind the
  // library subscription on every render.
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
  });

  const [lastMessage, setLastMessage] = useState<T | null>(null);

  const active = !!routeKey && routeKey !== "__open__";

  const handleSocketMessage = useCallback(
    (event: WebSocketEventMap["message"]) => {
      if (!active) {
        return;
      }
      let parsed: any;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!parsed || typeof parsed !== "object" || !("routeKey" in parsed)) {
        return;
      }
      if (parsed.routeKey === routeKey) {
        setLastMessage(parsed as T);
        onMessageRef.current?.(parsed as T);
      }
    },
    [active, routeKey]
  );

  const handleSocketOpen = useCallback(() => {
    onOpenRef.current?.();
  }, []);

  const { sendJsonMessage: sendMessageWebsocket, readyState } = useWebsocket(
    websocketUrl,
    {
      shouldReconnect: () => !didUnmount.current,
      reconnectAttempts: 50,
      reconnectInterval: (attemptNumber: number) => Math.min(1000 * 2 ** attemptNumber, 30000),
      onMessage: handleSocketMessage,
      onOpen: handleSocketOpen,
      // Share one connection across every hook instance.
      share: true,
    },
    enabled
  );

  const register = useCallback(() => {
    if (!userId || readyState !== ReadyState.OPEN || !active) {
      return;
    }
    sendMessageWebsocket({ token, routeKey, ...(sessionId ? { sessionId } : {}) });
  }, [userId, readyState, active, routeKey, token, sessionId, sendMessageWebsocket]);

  // Register on connect / reconnect; disconnect on unmount (only when we registered).
  useEffect(() => {
    didUnmount.current = false;
    if (!userId || readyState !== ReadyState.OPEN || !active) {
      return;
    }
    sendMessageWebsocket({ token, routeKey, ...(sessionId ? { sessionId } : {}) });
    return () => {
      sendMessageWebsocket({
        token,
        routeKey,
        ...(sessionId ? { sessionId } : {}),
        disconnect: true,
      });
      didUnmount.current = true;
    };
  }, [userId, readyState, active, routeKey, sessionId]);

  return { lastMessage, readyState, register };
}
